/**
 * O que dá para fazer no diagrama **sem montar o desenho** — cada coisa numa
 * seção que abre e fecha.
 *
 * Eram sete abas numa fileira apertada, e a barra ficava longa demais para
 * achar qualquer coisa. Agora são títulos com setinha, como a árvore de
 * configurações do VS Code: fechados, ocupam uma linha cada, e **só buscam
 * dados quando alguém os abre** (o `Secao` não monta o que está fechado).
 *
 * **O sistema é o que destrava a conferência.** Conferir e concluir não são do
 * diagrama, são do *sistema dentro do projeto* — "a injeção deste projeto está
 * revisada". Por isso a seção junta os dois: vincular e marcar.
 *
 * **O que é de componente mora na montagem, não aqui.** A lista dos componentes
 * (ficha, cor e desenho interno em lote, RN-15, agrupar) e a inclusão de ligações
 * em lote estavam neste painel e a produção não via sentido nelas (relatório de
 * 28/09/2026). Tudo isso se faz na montagem, com o desenho à vista — e o atalho
 * para ela fica no topo do painel, em destaque.
 *
 * A geometria do terminal (altura, deslocar, posição, ramificação) tem tela
 * própria em `TerminaisDoComponente`, que se apoia no "Ver como ficaria" do
 * painel do desenho.
 */

import { useMemo, useState } from "react"
import {
  metadadosDe, usarAnexosSvg, usarDefinirModelosDoDiagrama, usarDescreverAnexo, usarModelosDoDiagrama,
  usarDesvincularSistema, usarEditarDiagrama, usarExcluidosDoDiagrama,
  usarExcluirAnexo, usarMarcarRevisao, usarObsDiagrama, usarOrdenarAnexos, usarRestaurarDoDiagrama,
  usarSistemasDoDiagrama, usarTrocarCorDoDiagrama, usarVincularDiagrama,
  usarVincularSistema, usarVinculoDoDiagrama,
  type DiagramaResponse, type MarcaDeRevisao, type MetadadosDoDiagrama,
} from "@/api/diagramas"
import { usarModelosDoProjeto } from "@/api/modelos"
import { DesenhoDoDiagrama } from "./DesenhoDoDiagrama"
import { VerificacaoDoDiagrama } from "./VerificacaoDoDiagrama"
import { SeletorDeCorDeFuncao } from "./SeletorDeCorDeFuncao"
import { usarCatalogoDeSistemas } from "./sistemasDoCatalogo"
import { SeletorComBusca } from "@/kit/SeletorComBusca"
import { Secao } from "@/kit/Secao"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"

const ajuda: React.CSSProperties = { fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }

/**
 * As seções, na ordem em que aparecem entre "Publicação e revisão" e "Gerar e
 * duplicar" do painel. As que mais se usam ficam em cima.
 */
export function DetalhesDoDiagrama({ projetoId, diagrama }: {
  projetoId: number
  diagrama: DiagramaResponse
}) {
  return (
    <>
      <Secao titulo="Desenho publicado" chave="diagrama-desenho">
        <DesenhoDoDiagrama projetoId={projetoId} diagramaId={diagrama.id} />
        <VerificacaoDoDiagrama diagramaId={diagrama.id} />
      </Secao>

      <Secao titulo="Ficha e observação" chave="diagrama-ficha">
        <FichaDoDiagrama diagrama={diagrama} projetoId={projetoId} />
        <ObservacaoDoDiagrama diagrama={diagrama} projetoId={projetoId} />
      </Secao>

      <Secao titulo="Modelos" chave="diagrama-modelos">
        <ModelosDoDiagrama diagrama={diagrama} projetoId={projetoId} />
      </Secao>

      <Secao titulo="Sistemas e conferência" chave="diagrama-sistemas">
        <SistemasDoDiagrama diagrama={diagrama} projetoId={projetoId} />
      </Secao>

      <Secao titulo="Anexos e cor do principal" chave="diagrama-anexos">
        <AnexosDoDiagrama diagrama={diagrama} projetoId={projetoId} />
      </Secao>

      <Secao titulo="Ligado a outro diagrama" chave="diagrama-vinculo">
        <VinculoDoDiagrama diagrama={diagrama} projetoId={projetoId} />
      </Secao>

      <Secao titulo="Lixeira dos componentes" chave="diagrama-lixeira">
        <LixeiraDoDiagrama diagrama={diagrama} projetoId={projetoId} />
      </Secao>
    </>
  )
}

type DaSecao = { projetoId: number; diagrama: DiagramaResponse }

function Erro({ erro }: { erro: unknown }) {
  if (erro == null) return null
  return (
    <div style={{
      padding: "9px 11px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
      background: erro instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
      color: erro instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
    }}>
      {erro instanceof ErroDaApi || erro instanceof Error ? erro.message : "Não foi possível."}
    </div>
  )
}

function ModelosDoDiagrama({ projetoId, diagrama }: DaSecao) {
  const modelos = usarModelosDoProjeto(projetoId)
  const definirModelos = usarDefinirModelosDoDiagrama(projetoId)
  const modelosGravados = usarModelosDoDiagrama(diagrama.id)
  // `projeto_modelo` tem uma linha por produto: o mesmo modelo pode vir repetido.
  const modelosDoProjeto = useMemo(() => {
    const vistos = new Map<number, { id: number; descricao: string }>()
    for (const m of modelos.data ?? []) if (!vistos.has(m.id)) vistos.set(m.id, { id: m.id, descricao: m.descricao })
    return [...vistos.values()].sort((a, b) => a.descricao.localeCompare(b.descricao, "pt-BR"))
  }, [modelos.data])

  const doDiagrama = useMemo(() => new Set(modelosGravados.data ?? []), [modelosGravados.data])
  // Sem os modelos gravados em mãos, marcar uma caixa apagaria os outros.
  const modelosProntos = modelosGravados.isSuccess && !definirModelos.isPending

  function alternarModelo(modeloId: number) {
    if (!modelosProntos) return
    const novos = new Set(doDiagrama)
    novos.has(modeloId) ? novos.delete(modeloId) : novos.add(modeloId)
    definirModelos.mutate({ id: diagrama.id, modelos: [...novos] })
  }

  return (
    <>
      <span style={ajuda}>
        A que modelos este diagrama serve. No site o diagrama aparece só nos modelos marcados
        aqui — sem modelo nenhum, ele não aparece em lugar nenhum. Cada clique grava na hora.
      </span>
      {modelosGravados.isError ? (
        <span role="alert" style={{ fontSize: 11.5, color: "var(--erro)" }}>
          Não foi possível ler os modelos deste diagrama — marcar agora apagaria os que já estão ligados.
        </span>
      ) : modelos.isPending || modelosGravados.isPending ? (
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>Carregando os modelos…</span>
      ) : modelosDoProjeto.length === 0 ? (
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
          Este projeto não tem modelo cadastrado — cadastre na aba Modelos.
        </span>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <span style={{ display: "flex", gap: 10, fontSize: 11, color: "var(--txt3)" }}>
            {doDiagrama.size} de {modelosDoProjeto.length} marcados
            {modelosDoProjeto.length > 1 && (
              <>
                <button type="button" disabled={!modelosProntos}
                        onClick={() => definirModelos.mutate({ id: diagrama.id, modelos: modelosDoProjeto.map((m) => m.id) })}
                        style={{ marginLeft: "auto", fontSize: 11, color: "var(--brand-on-soft)", fontWeight: 600 }}>
                  marcar todos
                </button>
                <button type="button" disabled={!modelosProntos}
                        onClick={() => definirModelos.mutate({ id: diagrama.id, modelos: [] })}
                        style={{ fontSize: 11, color: "var(--txt3)" }}>
                  nenhum
                </button>
              </>
            )}
          </span>
          {modelosDoProjeto.map((m) => (
            <label key={m.id} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "6px 9px",
              borderRadius: 7, fontSize: 12,
              background: doDiagrama.has(m.id) ? "var(--brand-soft)" : "var(--surface2)",
              color: doDiagrama.has(m.id) ? "var(--brand-on-soft)" : "var(--txt2)",
              cursor: modelosProntos ? "pointer" : "progress",
            }}>
              <input
                type="checkbox"
                checked={doDiagrama.has(m.id)}
                disabled={!modelosProntos}
                onChange={() => alternarModelo(m.id)}
              />
              <span style={{ flex: 1, minWidth: 0 }}>{m.descricao}</span>
            </label>
          ))}
        </div>
      )}
      <Erro erro={definirModelos.error} />
    </>
  )
}

function SistemasDoDiagrama({ projetoId, diagrama }: DaSecao) {
  const sistemas = usarSistemasDoDiagrama(diagrama.id)
  const vincularSistema = usarVincularSistema(projetoId, diagrama.id)
  const desvincular = usarDesvincularSistema(projetoId, diagrama.id)
  const marcar = usarMarcarRevisao(projetoId)

  const [novoSistema, setNovoSistema] = useState(0)
  const catalogoDeSistemas = usarCatalogoDeSistemas()
  const nomeDoSistema = (id: number) =>
    catalogoDeSistemas.data?.find((x) => x.id === id)?.nome || `sistema ${id}`
  const jaVinculados = useMemo(() => new Set(sistemas.data ?? []), [sistemas.data])
  const opcoesDeSistema = useMemo(
    () => (catalogoDeSistemas.data ?? [])
      .filter((x) => x.id > 0 && !jaVinculados.has(x.id))
      .map((x) => ({ id: x.id, rotulo: x.nome ?? "" }))
      .sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR")),
    [catalogoDeSistemas.data, jaVinculados])

  return (
    <>
      <span style={ajuda}>
        O sistema é o que permite conferir e concluir a revisão — e ela é do
        <b> sistema dentro do projeto</b>, não do diagrama isolado.
      </span>

      {sistemas.isPending ? (
        <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>Carregando…</span>
      ) : (sistemas.data ?? []).length === 0 ? (
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
          Nenhum sistema vinculado. Sem isso não há o que conferir nem concluir.
        </span>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(sistemas.data ?? []).map((sistemaId) => (
            <div key={sistemaId} style={{
              padding: "9px 11px", borderRadius: 8, background: "var(--surface2)",
              display: "flex", flexDirection: "column", gap: 7,
            }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  {nomeDoSistema(sistemaId)}
                  <span className="num" style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 400, color: "var(--txt3)" }}>
                    {sistemaId}
                  </span>
                </span>
                <button
                  onClick={() => desvincular.mutate(sistemaId)}
                  disabled={desvincular.isPending}
                  title="Tira o sistema deste diagrama. Não apaga o sistema."
                  style={{
                    marginLeft: "auto", height: 22, padding: "0 8px", borderRadius: 5,
                    fontSize: 11, border: "1px solid var(--line2)",
                    background: "var(--surface)", color: "var(--txt3)", cursor: "pointer",
                  }}>
                  desvincular
                </button>
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {([
                  ["concluir", "Concluir"],
                  ["conferir", "Conferir"],
                  ["remover-conclusao", "Tirar conclusão"],
                  ["remover-conferencia", "Tirar conferência"],
                ] as const).map(([marca, rotulo]) => (
                  <button
                    key={marca}
                    onClick={() => marcar.mutate({ marca: marca as MarcaDeRevisao, sistemaId })}
                    disabled={marcar.isPending}
                    style={{
                      height: 26, padding: "0 9px", borderRadius: 6, fontSize: 11,
                      fontWeight: 600, border: "1px solid var(--line2)",
                      background: "var(--surface)", color: "var(--txt2)",
                    }}
                  >
                    {rotulo}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Era um campo de id numérico: ninguém achava o sistema para vincular
          (relatório de 28/09/2026). Agora é pelo nome, no mesmo catálogo que o
          legado usava — os sistemas em português, `base_id = 1`. */}
      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <SeletorComBusca
            itens={opcoesDeSistema}
            valor={novoSistema}
            aoMudar={setNovoSistema}
            carregando={catalogoDeSistemas.isPending}
            placeholder="Procure o sistema (ex.: injeção eletrônica)"
            rotuloAcessivel="Sistema para vincular ao diagrama"
          />
        </span>
        <button
          onClick={() => { vincularSistema.mutate(novoSistema); setNovoSistema(0) }}
          disabled={novoSistema <= 0 || vincularSistema.isPending}
          style={{
            height: 28, padding: "0 11px", borderRadius: 7, fontSize: 12, fontWeight: 600,
            background: novoSistema > 0 ? "var(--brand)" : "var(--surface)",
            color: novoSistema > 0 ? "#FFFFFF" : "var(--txt3)",
            border: novoSistema > 0 ? undefined : "1px solid var(--line2)",
          }}
        >
          Vincular
        </button>
      </span>
      <Erro erro={vincularSistema.error ?? desvincular.error ?? marcar.error} />
    </>
  )
}

function LixeiraDoDiagrama({ projetoId, diagrama }: DaSecao) {
  const excluidos = usarExcluidosDoDiagrama(diagrama.id)
  const restaurar = usarRestaurarDoDiagrama(projetoId, diagrama.id)

  return (
    <>
      <span style={ajuda}>
        O que foi tirado deste diagrama. A exclusão aqui é lógica (RN-15): nada some do banco,
        e dá para trazer de volta.
      </span>

      {excluidos.isPending ? (
        <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>Carregando…</span>
      ) : (excluidos.data ?? []).length === 0 ? (
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>Nada foi tirado deste diagrama.</span>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(excluidos.data ?? []).map((x) => (
            <div key={x.diagramaComponenteId} style={{
              padding: "8px 10px", borderRadius: 8, background: "var(--surface2)",
              display: "flex", flexDirection: "column", gap: 5,
            }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                  {x.titulo || <span className="num">componente {x.componenteId}</span>}
                  <span style={{ fontSize: 10.5, color: "var(--txt3)" }}> · {x.posicao}</span>
                </span>
                <button
                  onClick={() => restaurar.mutate({ tipo: "componentes", id: x.diagramaComponenteId })}
                  disabled={restaurar.isPending}
                  style={{
                    height: 24, padding: "0 9px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                    border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--txt2)",
                  }}
                >
                  restaurar
                </button>
              </span>
              {x.ligacoes.length > 0 && (
                <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
                  {x.ligacoes.length} {x.ligacoes.length === 1 ? "ligação junto" : "ligações junto"}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <Erro erro={restaurar.error} />
    </>
  )
}

function AnexosDoDiagrama({ projetoId, diagrama }: DaSecao) {
  const anexos = usarAnexosSvg(diagrama.id)
  const descrever = usarDescreverAnexo(projetoId, diagrama.id)
  const excluirAnexo = usarExcluirAnexo(projetoId, diagrama.id)
  const ordenar = usarOrdenarAnexos(projetoId, diagrama.id)
  const trocarCor = usarTrocarCorDoDiagrama(projetoId)
  const [cor, setCor] = useState(diagrama.cor || "#E6E7E8")

  return (
    <>
      <span style={ajuda}>
        <b>Anexos não são o desenho do diagrama</b> — são SVG que alguém pendurou aqui. São
        730 em produção. A ordem é a de exibição, e ela vale inteira: renumerar só uma parte
        deixa duas fatias começando em 1, que é o que o sistema antigo faz.
      </span>

      {anexos.isPending ? (
        <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>Carregando…</span>
      ) : (anexos.data ?? []).length === 0 ? (
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
          Este diagrama não tem anexo.
        </span>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {(anexos.data ?? []).map((a, i, lista) => (
            <div key={a.id} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "5px 7px",
              borderRadius: 7, background: "var(--surface3)",
            }}>
              <span className="num" style={{ flex: "0 0 22px", fontSize: 11, color: "var(--txt3)" }}>
                {a.ordem}
              </span>
              <input
                defaultValue={a.descricao ?? ""}
                onBlur={(e) => {
                  const d = e.target.value
                  if (d !== (a.descricao ?? "")) descrever.mutate({ svgId: a.id, descricao: d })
                }}
                placeholder="sem descrição"
                aria-label={`Descrição do anexo ${a.id}`}
                style={{
                  flex: 1, minWidth: 0, padding: "3px 6px", fontSize: 11.5,
                  border: "1px solid var(--line)", borderRadius: 5,
                  background: "var(--surface)", color: "var(--txt)",
                }}
              />
              <button
                title="Subir"
                disabled={i === 0 || ordenar.isPending}
                onClick={() => {
                  // A ordem vai inteira: a API recusa lista incompleta.
                  // Troca explicita: com noUncheckedIndexedAccess, o
                  // destructuring de indice vira number|undefined.
                  const ids = lista.map((x) => x.id)
                  const antes = ids[i - 1]!
                  ids[i - 1] = ids[i]!
                  ids[i] = antes
                  ordenar.mutate(ids)
                }}
                style={{ fontSize: 11, padding: "2px 6px" }}
              >
                ↑
              </button>
              <button
                title="Descer"
                disabled={i === lista.length - 1 || ordenar.isPending}
                onClick={() => {
                  const ids = lista.map((x) => x.id)
                  const atual = ids[i]!
                  ids[i] = ids[i + 1]!
                  ids[i + 1] = atual
                  ordenar.mutate(ids)
                }}
                style={{ fontSize: 11, padding: "2px 6px" }}
              >
                ↓
              </button>
              <button
                title="Excluir o anexo"
                disabled={excluirAnexo.isPending}
                onClick={() => excluirAnexo.mutate(a.id)}
                style={{ fontSize: 11, padding: "2px 6px", color: "var(--erro)" }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <span style={{
        marginTop: 6, paddingTop: 8, borderTop: "1px solid var(--line2)", ...ajuda,
      }}>
        <b>Cor do componente principal.</b> É do diagrama inteiro — 4.800 dos 4.829 estão no
        padrão. Trocar marca o diagrama para atualização; sem isso o site segue mostrando o
        desenho publicado com a cor velha.
      </span>

      <SeletorDeCorDeFuncao valor={cor} aoMudar={setCor} semCor={false}
                            rotulo="Cor do componente principal" />
      <span>
        <button
          onClick={() => trocarCor.mutate({ id: diagrama.id, cor })}
          disabled={trocarCor.isPending || cor.toUpperCase() === (diagrama.cor || "#E6E7E8").toUpperCase()}
          style={{ fontSize: 11.5, padding: "3px 9px" }}
        >
          {trocarCor.isPending ? "Trocando…" : "Trocar a cor"}
        </button>
      </span>
      <Erro erro={descrever.error ?? excluirAnexo.error ?? ordenar.error ?? trocarCor.error} />
    </>
  )
}

function VinculoDoDiagrama({ projetoId, diagrama }: DaSecao) {
  const vinculo = usarVinculoDoDiagrama(diagrama.id)
  const vincular = usarVincularDiagrama(projetoId, diagrama.id)
  const [outro, setOutro] = useState("")

  return (
    <>
      <span style={ajuda}>
        Diagrama grande demais para uma folha é quebrado em dois. O vínculo diz qual é a outra
        metade — sem ele, quem abre um dos dois não sabe que existe o resto.
      </span>

      {vinculo.isPending ? (
        <span style={{ fontSize: 11.5, color: "var(--txt2)" }}>Carregando…</span>
      ) : vinculo.data?.diagramaId2 ? (
        <div style={{
          padding: "10px 11px", borderRadius: 8, background: "var(--brand-soft)",
          color: "var(--brand-on-soft)", display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ flex: 1, fontSize: 12 }}>
            continua no diagrama{" "}
            <b className="num">{vinculo.data.diagramaId2}</b>
          </span>
        </div>
      ) : (
        <span style={{ fontSize: 11.5, color: "var(--txt3)" }}>
          Este diagrama não continua em nenhum outro.
        </span>
      )}

      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          value={outro}
          onChange={(e) => setOutro(e.target.value.replace(/\D/g, ""))}
          placeholder="id do diagrama"
          aria-label="Id do diagrama a ligar"
          style={{
            width: 130, height: 28, padding: "0 9px", borderRadius: 7,
            border: "1px solid var(--line2)", background: "var(--surface2)",
            fontSize: 12, fontFamily: "var(--fm)",
          }}
        />
        <button
          onClick={() => { vincular.mutate(Number(outro)); setOutro("") }}
          disabled={!outro || Number(outro) === diagrama.id || vincular.isPending}
          title={Number(outro) === diagrama.id
            ? "Um diagrama não continua nele mesmo."
            : "Liga os dois diagramas"}
          style={{
            height: 28, padding: "0 11px", borderRadius: 7, fontSize: 12, fontWeight: 600,
            background: outro && Number(outro) !== diagrama.id ? "var(--brand)" : "var(--surface)",
            color: outro && Number(outro) !== diagrama.id ? "#FFFFFF" : "var(--txt3)",
            border: outro && Number(outro) !== diagrama.id ? undefined : "1px solid var(--line2)",
          }}
        >
          {vinculo.data?.diagramaId2 ? "Trocar" : "Ligar"}
        </button>
      </span>
      <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
        O id sai da lista ao lado — a coluna Diagrama.
      </span>
      <Erro erro={vincular.error} />
    </>
  )
}

/** A observação livre do diagrama — o que aparece na coluna Observação da lista. */
function ObservacaoDoDiagrama({ projetoId, diagrama }: DaSecao) {
  const anotar = usarObsDiagrama(projetoId)
  const [obs, setObs] = useState(diagrama.obs ?? "")

  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
      <span style={{ fontSize: 10, color: "var(--txt3)", textTransform: "uppercase", letterSpacing: 0.4 }}>
        Observação
      </span>
      <textarea
        value={obs}
        onChange={(e) => setObs(e.target.value)}
        rows={3}
        style={{
          width: "100%", padding: "7px 9px", borderRadius: 7, resize: "vertical",
          border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5, lineHeight: 1.45,
        }}
      />
      {obs !== (diagrama.obs ?? "") && (
        <button
          onClick={() => anotar.mutate({ id: diagrama.id, obs })}
          disabled={anotar.isPending}
          style={{
            alignSelf: "flex-start", height: 28, padding: "0 11px", borderRadius: 7,
            background: "var(--brand)", color: "#FFFFFF", fontSize: 12, fontWeight: 600,
          }}
        >
          {anotar.isPending ? "Gravando…" : "Gravar observação"}
        </button>
      )}
      <Erro erro={anotar.error} />
    </label>
  )
}

/**
 * Os metadados do diagrama — o `Editar` do sistema antigo.
 *
 * **O `PUT` substitui os sete de uma vez**, então o formulário parte do que a
 * linha trouxe e devolve tudo, inclusive `pinos` e o desenho interno do
 * componente principal, que não aparecem aqui. Foi por isso que os dois passaram
 * a vir no `GET`: sem eles, mudar a posição apagava os dois, calado.
 *
 * **Versão fica de fora de propósito.** Trocar a versão troca o caminho do motor
 * inteiro, e o diagrama foi desenhado por um deles — é operação de migração, não
 * de formulário.
 */
function FichaDoDiagrama({ projetoId, diagrama }: DaSecao) {
  const editar = usarEditarDiagrama(projetoId)
  const [corpo, setCorpo] = useState<MetadadosDoDiagrama>(() => metadadosDe(diagrama))
  const [recado, setRecado] = useState<string | null>(null)

  const entrada: React.CSSProperties = {
    padding: "4px 7px", fontSize: 11.5, border: "1px solid var(--line)",
    borderRadius: 5, background: "var(--surface)", color: "var(--txt)",
  }
  const rotulo: React.CSSProperties = {
    fontSize: 10, color: "var(--txt3)", textTransform: "uppercase", letterSpacing: 0.4,
  }

  return (
    <>
      <span style={ajuda}>
        O que o diagrama <b>é</b>: onde ele fica no desenho, em que modo, e a que sistema
        pertence. Gravar aqui não redesenha — o traço publicado só muda no Redesenhar.
      </span>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={rotulo}>Posição</span>
          <select value={corpo.posicao} onChange={(e) => setCorpo({ ...corpo, posicao: e.target.value })}
                  style={{ ...entrada, width: 110 }}>
            <option value="center">Centro</option>
            <option value="left">Esquerda</option>
            <option value="right">Direita</option>
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={rotulo}>Modo</span>
          <select value={corpo.modo} onChange={(e) => setCorpo({ ...corpo, modo: Number(e.target.value) })}
                  style={{ ...entrada, width: 120 }}>
            <option value={0}>Normal</option>
            <option value={1}>Interno</option>
            <option value={2}>Agrupado</option>
          </select>
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={rotulo}>Sistema</span>
          <input type="number" value={corpo.sistemaId}
                 onChange={(e) => setCorpo({ ...corpo, sistemaId: Number(e.target.value) })}
                 style={{ ...entrada, width: 84 }} />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}
               title="O desenho interno do componente principal.">
          <span style={rotulo}>Desenho interno</span>
          <input type="number" value={corpo.diagramaDesenhoInternoId}
                 onChange={(e) => setCorpo({ ...corpo, diagramaDesenhoInternoId: Number(e.target.value) })}
                 style={{ ...entrada, width: 84 }} />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 3 }}
               title="Os pinos que este diagrama recorta. Vazio = o componente inteiro.">
          <span style={rotulo}>Pinos</span>
          <input value={corpo.pinos ?? ""}
                 onChange={(e) => setCorpo({ ...corpo, pinos: e.target.value || null })}
                 style={{ ...entrada, width: 200 }} />
        </label>

        <button
          onClick={() => {
            setRecado(null)
            editar.mutate({ id: diagrama.id, corpo }, {
              onSuccess: () => setRecado("Gravado."),
              onError: (e) => setRecado(
                e instanceof ErroDeTrava || e instanceof ErroDaApi
                  ? e.message : "Não foi possível gravar."),
            })
          }}
          disabled={editar.isPending}
          style={{ ...entrada, cursor: "pointer", fontWeight: 600 }}>
          {editar.isPending ? "Gravando…" : "Gravar ficha"}
        </button>
      </div>

      <span style={{ fontSize: 10.5, color: "var(--txt3)" }}>
        versão {diagrama.versao} · componente {diagrama.componenteCodigo} — trocar a versão é
        operação de migração, e não sai daqui.
      </span>

      {recado && <p style={{ margin: 0, fontSize: 11.5, color: "var(--txt2)" }}>{recado}</p>}
    </>
  )
}
